// 文件存储工具 - 使用 File System Access API

class FileStorage {
  constructor() {
    this.directoryHandle = null
    this.directoryName = ''
    this.db = null
  }

  // 检查浏览器是否支持 File System Access API
  isSupported() {
    return 'showDirectoryPicker' in window
  }

  // 初始化 IndexedDB
  async initDB() {
    if (this.db) return this.db
    
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('dapi-storage', 1)
      
      request.onerror = () => reject(request.error)
      request.onsuccess = () => {
        this.db = request.result
        resolve(this.db)
      }
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains('handles')) {
          db.createObjectStore('handles', { keyPath: 'id' })
        }
      }
    })
  }

  // 选择目录
  async selectDirectory() {
    if (!this.isSupported()) {
      throw new Error('您的浏览器不支持 File System Access API，请使用 Chrome 或 Edge 浏览器')
    }

    try {
      this.directoryHandle = await window.showDirectoryPicker()
      this.directoryName = this.directoryHandle.name
      
      // 保存目录句柄到 IndexedDB 以便恢复
      await this.saveDirectoryHandle()
      
      return true
    } catch (error) {
      if (error.name === 'AbortError') {
        return false
      }
      throw error
    }
  }

  // 保存目录句柄到 IndexedDB
  async saveDirectoryHandle() {
    try {
      await this.initDB()
      
      // 检查对象存储是否存在
      if (!this.db.objectStoreNames.contains('handles')) {
        console.log('对象存储不存在，无法保存')
        return
      }
      
      return new Promise((resolve, reject) => {
        try {
          const transaction = this.db.transaction(['handles'], 'readwrite')
          const store = transaction.objectStore('handles')
          const request = store.put({
            id: 'workspace',
            handle: this.directoryHandle,
            name: this.directoryName
          })
          
          request.onsuccess = () => resolve()
          request.onerror = () => reject(request.error)
        } catch (err) {
          reject(err)
        }
      })
    } catch (error) {
      console.log('保存目录句柄失败:', error)
    }
  }

  // 从 IndexedDB 恢复目录句柄
  async restoreDirectory() {
    try {
      await this.initDB()
      
      // 检查对象存储是否存在
      if (!this.db.objectStoreNames.contains('handles')) {
        console.log('对象存储不存在，跳过恢复')
        return false
      }
      
      const data = await new Promise((resolve, reject) => {
        try {
          const transaction = this.db.transaction(['handles'], 'readonly')
          const store = transaction.objectStore('handles')
          const request = store.get('workspace')
          
          request.onsuccess = () => resolve(request.result)
          request.onerror = () => reject(request.error)
        } catch (err) {
          reject(err)
        }
      })
      
      if (data && data.handle) {
        // 验证权限
        const permission = await data.handle.requestPermission({ mode: 'readwrite' })
        if (permission === 'granted') {
          this.directoryHandle = data.handle
          this.directoryName = data.name
          return true
        }
      }
      return false
    } catch (error) {
      console.log('恢复目录句柄失败:', error)
      return false
    }
  }

  // 检查是否已选择目录
  hasDirectory() {
    return this.directoryHandle !== null
  }

  // 获取目录名称
  getDirectoryName() {
    return this.directoryName
  }

  // 读取文件
  async readFile(filename) {
    if (!this.directoryHandle) {
      throw new Error('请先选择工作目录')
    }

    try {
      const fileHandle = await this.directoryHandle.getFileHandle(filename)
      const file = await fileHandle.getFile()
      return await file.text()
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return null
      }
      throw error
    }
  }

  // 写入文件
  async writeFile(filename, content) {
    if (!this.directoryHandle) {
      throw new Error('请先选择工作目录')
    }

    const fileHandle = await this.directoryHandle.getFileHandle(filename, { create: true })
    const writable = await fileHandle.createWritable()
    await writable.write(content)
    await writable.close()
    return true
  }

  // 删除文件
  async deleteFile(filename) {
    if (!this.directoryHandle) {
      throw new Error('请先选择工作目录')
    }

    try {
      await this.directoryHandle.removeEntry(filename)
      return true
    } catch (error) {
      if (error.name === 'NotFoundError') {
        return false
      }
      throw error
    }
  }

  // 列出目录中的文件
  async listFiles() {
    if (!this.directoryHandle) {
      throw new Error('请先选择工作目录')
    }

    const files = []
    for await (const [name, handle] of this.directoryHandle.entries()) {
      files.push({ name, kind: handle.kind })
    }
    return files
  }
}

// 导出单例
export const fileStorage = new FileStorage()
